import {
  AlertTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  calculateFailoverSeverity,
  getFailoverSeverityColor,
  getFailoverSeverityIcon,
} from "@/utils/smart-routing-alerts"

interface FailoverBadgeProps {
  failoverRate: number | null
  className?: string
}

export const FailoverBadge = ({
  failoverRate,
  className,
}: FailoverBadgeProps) => {
  const { t } = useTranslation()

  if (failoverRate === null) {
    return <span className={className}>—</span>
  }

  const severity = calculateFailoverSeverity(failoverRate)
  const color = getFailoverSeverityColor(severity)
  const icon = getFailoverSeverityIcon(severity)

  const getSeverityIcon = () => {
    switch (severity) {
      case "excellent":
        return (
          <CheckCircleIcon
            size={14}
            className="text-green-600"
          />
        )
      case "acceptable":
        return (
          <CheckCircleIcon
            size={14}
            className="text-lime-600"
          />
        )
      case "high":
        return (
          <AlertTriangleIcon
            size={14}
            className="text-orange-600"
          />
        )
      case "critical":
      case "emergency":
        return (
          <XCircleIcon size={14} className="text-red-600" />
        )
      default:
        return null
    }
  }

  const getSeverityMessage = () => {
    switch (severity) {
      case "excellent":
        return t("smartRouting.failover.excellent")
      case "acceptable":
        return t("smartRouting.failover.acceptable")
      case "high":
        return t("smartRouting.failover.high")
      case "critical":
        return t("smartRouting.failover.critical")
      case "emergency":
        return t("smartRouting.failover.emergency")
      default:
        return ""
    }
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className={className}>
          <Badge
            variant="none"
            className="flex h-fit items-center justify-start gap-1.5 rounded-full px-2 py-1"
            style={{
              backgroundColor: `${color}15`,
              border: `1px solid ${color}40`,
            }}
          >
            {getSeverityIcon()}
            <span
              className="text-sm font-medium"
              style={{ color }}
            >
              {failoverRate.toFixed(1)}%
            </span>
          </Badge>
        </div>
      </TooltipTrigger>
      <TooltipContent
        style={{
          backgroundColor: "var(--whitelabel-background)",
        }}
        className="border-0 text-white"
      >
        <div className="space-y-1">
          <p className="font-medium text-white">
            {icon} {getSeverityMessage()}
          </p>
          {severity === "emergency" && (
            <p className="text-xs text-gray-200">
              {t("smartRouting.failover.emergencyMessage")}
            </p>
          )}
          {severity === "critical" && (
            <p className="text-xs text-gray-200">
              {t("smartRouting.failover.criticalMessage")}
            </p>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  )
}
